/** @jsx isolateComponent */

import Icon from '@zapier/design-system/src/components/display/Icon';
import Spinner from '@zapier/design-system/src/components/display/Spinner';
import TextInput from '@zapier/design-system/src/components/forms/TextInput';
import { camelCaseAriaAttrs } from '@zapier/design-system/src/utils/formatAriaAttrKeys';
import { isolateComponent, labelStyles } from '@zapier/common-utils';

import { GetInputPropsOptions } from 'downshift';

export type TypeaheadInputProps<T = any> = {
  /**
   * Browser `autocomplete` form field attribute.
   */
  autoComplete?: string;
  /**
   * Accessibility label that describes this component. This should be supplied
   * if no corresponding <label> element is used.
   */
  ariaLabel?: string;
  /**
   * Accessibility label referencing the ID of an element which describes this
   * component.
   */
  ariaLabelledBy: string | null;
  /**
   * Callback provided by Downshift which clears the typeahead selection.
   */
  clearSelection: () => void;
  /**
   * The text that renders in a `Tooltip` when the input `isDisabled`.
   * Only renders when `isDisabled`.
   */
  disabledText?: string;
  /**
   * Callback provided by Downshift which provides props which must be spread
   * into the `input` element.
   */
  getInputProps: (options: GetInputPropsOptions) => GetInputPropsOptions;
  /**
   * The current value of the input, provided by Downshift.
   */
  inputValue: string | null;
  /**
   * Displays an inline loading spinner in the place of the left icon if set to true.
   */
  isBusy?: boolean;
  /**
   * Disables the input and makes it non-interactive.
   */
  isDisabled?: boolean;
  /**
   * Indicates whether the field has an error.
   */
  isErrored?: boolean;
  /**
   * Optional callback that may be provided by Downshift to open the menu.
   */
  onFocus?: () => void;
  /**
   * Placeholder text to render inside the `input` element toggle when no item
   * has been selected.
   */
  placeholder: string;
  /**
   * The currently selected item.
   */
  selectedItem: T | null;
  /**
   * Callback that will be used to generate the string label to be rendered for
   * the currently selected item. Provided by Downshift.
   */
  getLabelForItem: (item: T) => string;
  /**
   * A render prop which receives a menu `item` and the `getLabelForItem`
   * function and returns a React element, which is an icon illustrating the
   * given menu item. The natural size for the icon is 30x30px. If not
   * provided, no icons will be rendered.
   */
  renderIcon?: (
    item: T,
    getLabelForItem: (item: T) => string
  ) => React.ReactElement | null;
  /**
   * The rendered size of the input.
   */
  size?: 'small' | 'medium';
};

const Styles = labelStyles(__filename, {
  clearSelectionButton: {
    alignItems: 'center',
    cursor: 'pointer',
    display: 'flex',
    justifyContent: 'center',
    width: '100%',
    height: '100%',
  },
});

/**
 * The icon which sits inside the input field.
 */
const ClearSelectionButton = ({
  clearSelection,
  isFocused,
  isHovered,
}: {
  clearSelection: () => void;
  isFocused: boolean;
  isHovered: boolean;
}) => {
  // If not focused or hovered it should be a specific color,
  // otherwise it should inherit its color.
  const iconColor = !(isFocused || isHovered) ? 'grey500' : undefined;
  return (
    <button
      css={Styles.clearSelectionButton}
      onClick={clearSelection}
      title="Clear Selection"
    >
      <Icon
        canAcceptPointerEvents={false}
        color={iconColor}
        name="x"
        size={20}
      />
    </button>
  );
};

type InputIconProps = TypeaheadInputProps & {
  iconSize: number;
};
/**
 * The icon which sits inside the input field.
 */
const InputIcon = (props: InputIconProps) => {
  return props.renderIcon && props.selectedItem ? (
    props.renderIcon(props.selectedItem, props.getLabelForItem)
  ) : (
    // Search icon also handles the use case where an item has been
    // selected but props.renderIcon has not been specified.
    <Icon name="magnifyingGlass" size={props.iconSize} />
  );
};

const TypeaheadInput = (props: TypeaheadInputProps) => {
  const showClearSelectionButton = props.selectedItem && !props.isDisabled;

  return (
    <TextInput
      {...camelCaseAriaAttrs(
        props.getInputProps({
          autoComplete: props.autoComplete,
          // @ts-ignore this is valid, but downshift TS types are wrong
          ariaLabelledBy: props.ariaLabelledBy,
          onFocus: props.onFocus,
        })
      )}
      ariaLabel={props.ariaLabel}
      autocomplete="off"
      disabledText={props.disabledText}
      isDisabled={props.isDisabled}
      isErrored={props.isErrored}
      placeholder={props.placeholder}
      renderIconAfter={({
        isFocused,
        isHovered,
      }: {
        isFocused: boolean;
        isHovered: boolean;
      }) =>
        showClearSelectionButton && (
          <ClearSelectionButton
            clearSelection={props.clearSelection}
            isFocused={isFocused}
            isHovered={isHovered}
          />
        )
      }
      renderIconBefore={({ iconSize }: { iconSize: number }) => {
        if (props.isBusy) {
          return <Spinner />;
        }
        return <InputIcon {...props} iconSize={iconSize} />;
      }}
      size={props.size}
    />
  );
};

TypeaheadInput.defaultProps = {
  size: 'small',
};

export default TypeaheadInput;
